/**
 * TrustGrid Audit Trail Page
 *
 * Displays 21 CFR Part 11 compliant audit trail for TrustGrid operations.
 * Shows CREATE, UPDATE, DELETE actions on connectors, projects, etc.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Tag, Tooltip, Modal, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { api } from '../../../utils/apiClient';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import { TGDataTable } from '../../../components/common';
import ProjectSelector from './ProjectSelector';
import AuditFilterBar from './AuditFilterBar';
import { showError } from '../../../utils/notifications';
import { colors } from '../../../theme/designTokens';

function AuditTrail() {
  const location = useLocation();

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Audit Trail', path: '/trustgrid/audit' }];
  }, [location.state]);

  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [detailsModal, setDetailsModal] = useState({ visible: false, audit: null });

  const fetchAudits = async () => {
    setLoading(true);
    try {
      // Fetch ALL audits (client-side pagination/filtering)
      const res = await api.get('/trustgrid/v1/audit-trail', {
        params: {
          page: 1,
          per_page: 10000, // Get all records
        },
      });

      setAudits(res.data.audits || []);
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudits();
  }, []);

  const showDetails = (audit) => {
    setDetailsModal({ visible: true, audit });
  };

  /**
   * Filter audits by date range (external filter)
   */
  const filteredAudits = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return audits;

    const [startDate, endDate] = dateRange;
    const startTime = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const endTime = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;

    return audits.filter((audit) => {
      if (!audit.timestamp) return false;
      const auditTime = new Date(audit.timestamp).getTime();

      if (startTime && auditTime < startTime) return false;
      if (endTime && auditTime > endTime) return false;

      return true;
    });
  }, [audits, dateRange]);

  /**
   * Clear all external filters
   */
  const handleClearFilters = () => {
    setDateRange(null);
  };

  /**
   * Generate dynamic filter options from data
   */
  const actionFilters = useMemo(() => {
    if (!audits || audits.length === 0) return [];
    const actions = [...new Set(audits.map((a) => a.action).filter((a) => a))].sort();
    return actions.map((action) => ({ label: action, value: action }));
  }, [audits]);

  const entityTypeFilters = useMemo(() => {
    if (!audits || audits.length === 0) return [];
    const types = [...new Set(audits.map((a) => a.entity_type).filter((t) => t))].sort();
    return types.map((type) => ({ label: type, value: type }));
  }, [audits]);

  const userFilters = useMemo(() => {
    if (!audits || audits.length === 0) return [];
    const users = [...new Set(audits.map((a) => a.user_name).filter((u) => u))].sort();
    return users.map((user) => ({ label: user, value: user }));
  }, [audits]);

  const ipFilters = useMemo(() => {
    if (!audits || audits.length === 0) return [];
    const ips = [...new Set(audits.map((a) => a.ip_address).filter((ip) => ip))].sort();
    return ips.map((ip) => ({ label: ip, value: ip }));
  }, [audits]);

  const orgFilters = useMemo(() => {
    if (!audits || audits.length === 0) return [];
    const orgs = [...new Set(audits.map((a) => a.organization).filter((o) => o))].sort();
    return orgs.map((org) => ({ label: org, value: org }));
  }, [audits]);

  /**
   * Column definitions for TGDataTable
   */
  const columns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      fixed: 'left',
      render: (id) => <span style={{ fontFamily: 'Monaco, monospace', fontSize: 11 }}>#{id}</span>,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'timestamp',
      title: 'Timestamp',
      dataIndex: 'timestamp',
      width: 180,
      render: (timestamp) => <span style={{ fontSize: '12px' }}>{timestamp}</span>,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'user_name',
      title: 'User',
      dataIndex: 'user_name',
      width: 150,
      render: (name, record) => (
        <Tooltip title={record.user_email}>
          <span>{name}</span>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: userFilters,
    },
    {
      key: 'action',
      title: 'Action',
      dataIndex: 'action',
      width: 100,
      align: 'center',
      render: (action) => {
        let color = 'default';
        if (action === 'CREATE') color = 'green';
        if (action === 'UPDATE') color = 'blue';
        if (action === 'DELETE') color = 'red';
        return <Tag color={color}>{action}</Tag>;
      },
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: actionFilters,
    },
    {
      key: 'entity_type',
      title: 'Entity Type',
      dataIndex: 'entity_type',
      width: 140,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: entityTypeFilters,
    },
    {
      key: 'entity_id',
      title: 'Entity ID',
      dataIndex: 'entity_id',
      width: 100,
      align: 'center',
      render: (id) => <span style={{ fontFamily: 'Monaco, monospace', fontSize: 11 }}>{id}</span>,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'entity_name',
      title: 'Entity Name',
      dataIndex: 'entity_name',
      width: 200,
      render: (name) => <strong>{name}</strong>,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'change_summary',
      title: 'Change Summary',
      dataIndex: 'change_summary',
      ellipsis: true,
      render: (summary) => (
        <Tooltip title={summary}>
          <span>{summary}</span>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'ip_address',
      title: 'IP Address',
      dataIndex: 'ip_address',
      width: 140,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: ipFilters,
    },
    {
      key: 'session_id',
      title: 'Session ID',
      dataIndex: 'session_id',
      width: 120,
      ellipsis: true,
      render: (sessionId) => (
        <Tooltip title={sessionId}>
          <span style={{ fontFamily: 'Monaco, monospace', fontSize: 10 }}>
            {sessionId ? sessionId.substring(0, 12) + '...' : '—'}
          </span>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'organization',
      title: 'Organization',
      dataIndex: 'organization',
      width: 150,
      render: (org) => org || '—',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: orgFilters,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="View Details">
          <a onClick={() => showDetails(record)} style={{ color: colors.primary[500] }}>
            <EyeOutlined /> Details
          </a>
        </Tooltip>
      ),
    },
  ];

  return (
    <TrustGridPage
      title="TrustGrid Audit Trail"
      subtitle="21 CFR Part 11 compliant audit trail for all TrustGrid operations"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchAudits}
    >
      <ProjectSelector />

      <TGDataTable
        data={filteredAudits}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination="client"
        pageSize={50}
        showExport
        showColumnConfig
        showFilters
        exportFilename="audit-trail"
        presetKey="trustgrid.audit"
        onRefresh={fetchAudits}
        externalFilterCount={dateRange && dateRange.length === 2 ? 1 : 0}
        onClearExternalFilters={handleClearFilters}
        toolbar={<AuditFilterBar dateRange={dateRange} onDateRangeChange={setDateRange} />}
      />

      {/* Details Modal */}
      <Modal
        title="Audit Entry Details"
        open={detailsModal.visible}
        onCancel={() => setDetailsModal({ visible: false, audit: null })}
        footer={[
          <Button key="close" onClick={() => setDetailsModal({ visible: false, audit: null })}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {detailsModal.audit && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <h3>Basic Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '8px' }}>
                <strong>Timestamp:</strong>
                <span>{detailsModal.audit.timestamp}</span>

                <strong>User:</strong>
                <span>
                  {detailsModal.audit.user_name} ({detailsModal.audit.user_email})
                </span>

                <strong>Action:</strong>
                <Tag
                  color={
                    detailsModal.audit.action === 'CREATE'
                      ? 'green'
                      : detailsModal.audit.action === 'UPDATE'
                      ? 'blue'
                      : 'red'
                  }
                >
                  {detailsModal.audit.action}
                </Tag>

                <strong>Entity:</strong>
                <span>
                  {detailsModal.audit.entity_type} - {detailsModal.audit.entity_name}
                </span>

                <strong>IP Address:</strong>
                <span>{detailsModal.audit.ip_address}</span>

                <strong>Session ID:</strong>
                <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                  {detailsModal.audit.session_id}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h3>Change Summary</h3>
              <p>{detailsModal.audit.change_summary}</p>
            </div>

            {detailsModal.audit.fields_changed && detailsModal.audit.fields_changed.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h3>Fields Changed</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {detailsModal.audit.fields_changed.map((field) => (
                    <Tag key={field}>{field}</Tag>
                  ))}
                </div>
              </div>
            )}

            {detailsModal.audit.before_value && (
              <div style={{ marginBottom: '16px' }}>
                <h3>Before Values</h3>
                <pre
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(detailsModal.audit.before_value, null, 2)}
                </pre>
              </div>
            )}

            {detailsModal.audit.after_value && (
              <div>
                <h3>After Values</h3>
                <pre
                  style={{
                    backgroundColor: '#e3f2fd',
                    padding: '12px',
                    borderRadius: '4px',
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(detailsModal.audit.after_value, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </TrustGridPage>
  );
}

export default AuditTrail;
